import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { useWorkspaceStore, selectCapabilitiesList, selectActiveWorkspace } from '@/stores/workspaceStore';
import { useCapabilities } from '@/hooks/useCapabilities';
import { collectionsApi } from '@/lib/api';
import AppShell from '@/components/layout/AppShell';

// ─── Icon ─────────────────────────────────────────────────────────────────────

const Icon: React.FC<{ path: React.ReactNode; className?: string }> = ({ path, className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
    className={className} aria-hidden="true">
    {path}
  </svg>
);

// ─── Icon paths ───────────────────────────────────────────────────────────────

const ICONS = {
  notes: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>,
  thinking: <><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/><circle cx="12" cy="12" r="10"/></>,
  graph: <><circle cx="12" cy="12" r="2"/><circle cx="4" cy="6" r="2"/><circle cx="20" cy="6" r="2"/><circle cx="4" cy="18" r="2"/><circle cx="20" cy="18" r="2"/><line x1="12" y1="12" x2="4" y2="6"/><line x1="12" y1="12" x2="20" y2="6"/><line x1="12" y1="12" x2="4" y2="18"/><line x1="12" y1="12" x2="20" y2="18"/></>,
  decisions: <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3z"/></>,
  reviews: <><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></>,
  people: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
  projects: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
  capabilities: <><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></>,
  capture: <path d="M12 5v14M5 12h14" />,
  search: <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
  arrow: <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
};

// ─── Module card ──────────────────────────────────────────────────────────────

interface ModuleCardProps {
  label: string;
  description: string;
  icon: React.ReactNode;
  path?: string;
  soon?: boolean;
  stat?: string | number;
  statLabel?: string;
  accent?: boolean;
}

const ModuleCard: React.FC<ModuleCardProps> = ({
  label, description, icon, path, soon, stat, statLabel, accent
}) => {
  const history = useHistory();

  return (
    <button
      type="button"
      onClick={soon || !path ? undefined : () => history.push(path)}
      className="card text-left w-full p-4 flex flex-col gap-3 group relative overflow-hidden"
      style={{
        cursor: soon ? 'default' : 'pointer',
        opacity: soon ? 0.55 : 1,
      }}
    >
      {/* Crimson accent glow on hover */}
      {!soon && (
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at top left, rgba(192,57,43,0.07) 0%, transparent 60%)' }}
        />
      )}

      <div className="flex items-start justify-between gap-2">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            background: accent ? 'var(--crimson-dim)' : 'var(--bg-high)',
            border: `1px solid ${accent ? 'var(--crimson-border)' : 'var(--border)'}`,
          }}
        >
          <span style={{ color: accent ? 'var(--crimson-light)' : 'var(--text-muted)' }}>
            {icon}
          </span>
        </div>
        {soon ? (
          <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
            style={{ background: 'var(--bg-high)', color: 'var(--text-faint)', fontFamily: 'var(--font-display)', border: '1px solid var(--border)' }}>
            Soon
          </span>
        ) : stat !== undefined ? (
          <div className="text-right">
            <div className="text-lg font-bold leading-none" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>
              {stat}
            </div>
            {statLabel && (
              <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {statLabel}
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div>
        <div className="text-sm font-semibold mb-0.5 group-hover:text-white transition-colors"
          style={{ color: 'var(--text)', fontFamily: 'var(--font-display)', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
          {label}
        </div>
        <div className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          {description}
        </div>
      </div>
    </button>
  );
};

// ─── Loop flow ────────────────────────────────────────────────────────────────

const LOOP_STEPS: { label: string; sub: string; icon: React.ReactNode; active?: boolean }[] = [
  {
    label: 'Knowledge',
    sub: 'Notes · Sources',
    icon: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></>,
    active: true,
  },
  {
    label: 'Thinking',
    sub: 'Questions · Insights',
    icon: <><path d="M9 18h6"/><path d="M10 22h4"/><path d="M15.09 14c.18-1.16.85-2.13 2-3a5 5 0 1 0-7 0c1.15.87 1.82 1.84 2 3"/></>,
  },
  {
    label: 'Decisions',
    sub: 'Context · Options',
    icon: <><path d="M6 3v12"/><path d="M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/><path d="M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/><path d="M15 6a9 9 0 0 0-9 9"/></>,
  },
  {
    label: 'Action',
    sub: 'Projects · Goals',
    icon: <><path d="m5 3 14 9-14 9V3z"/></>,
  },
  {
    label: 'Capability',
    sub: 'Compound growth',
    icon: <><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></>,
  },
];

const LoopFlow: React.FC = () => (
  <div className="relative">
    {/* Connector line behind nodes */}
    <div className="absolute left-0 right-0 top-[18px] h-px hidden md:block pointer-events-none"
      style={{ background: 'linear-gradient(to right, transparent 0%, var(--border) 8%, var(--border) 92%, transparent 100%)' }} />

    <div className="relative flex flex-wrap md:flex-nowrap items-start justify-between gap-y-5 gap-x-2">
      {LOOP_STEPS.map((step, i) => {
        const isLast = i === LOOP_STEPS.length - 1;
        const isActive = step.active;
        return (
          <React.Fragment key={step.label}>
            <div className="flex flex-col items-center text-center min-w-0 flex-1 overflow-hidden px-1">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center mb-2 transition-colors flex-shrink-0"
                style={{
                  background: isActive ? 'var(--crimson)' : 'var(--bg-surface)',
                  border: `1px solid ${isActive ? 'var(--crimson)' : 'var(--border-mid)'}`,
                  boxShadow: isActive ? '0 0 0 4px rgba(192,57,43,0.12)' : 'none',
                  color: isActive ? '#fff' : 'var(--text-muted)',
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                  strokeLinecap="round" strokeLinejoin="round" className="w-[16px] h-[16px]" aria-hidden="true">
                  {step.icon}
                </svg>
              </div>
              <span
                className="text-[10px] font-bold uppercase truncate max-w-full"
                style={{
                  fontFamily: 'var(--font-display)',
                  letterSpacing: '0.14em',
                  color: isActive ? 'var(--text)' : 'var(--text-dim)',
                }}
              >
                {step.label}
              </span>
              <span
                className="text-[10px] mt-0.5 max-w-full leading-tight"
                style={{
                  color: 'var(--text-muted)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}
              >
                {step.sub}
              </span>
            </div>

            {!isLast && (
              <div className="hidden md:flex flex-shrink-0 items-center justify-center self-start pt-[10px]"
                style={{ color: 'var(--text-faint)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
                  <path d="M9 6l6 6-6 6"/>
                </svg>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

// ─── DashboardPage ────────────────────────────────────────────────────────────

const DashboardPage: React.FC = () => {
  const history = useHistory();
  const user = useAuthStore((s) => s.user);
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const capabilities = useWorkspaceStore(selectCapabilitiesList);
  const workspaceId = activeWorkspace?.id ?? null;
  const { isLoading } = useCapabilities(workspaceId, { pageSize: 20 });

  const { data: collectionsData, isLoading: collectionsLoading } = useQuery({
    queryKey: ['collections', workspaceId],
    queryFn: () => (workspaceId ? collectionsApi.list(workspaceId) : Promise.resolve([])),
    enabled: Boolean(workspaceId),
    staleTime: 60_000,
  });
  const collectionsCount = collectionsData?.length ?? 0;
  const capabilitiesCount = capabilities.length;
  const statsLoading = isLoading || collectionsLoading;
  const showStats = statsLoading || capabilitiesCount > 0 || collectionsCount > 0;
  const growingCount = capabilities.filter((c) => c.status === 'growing').length;
  const avgStrength = capabilitiesCount
    ? `${Math.round(capabilities.reduce((s, c) => s + (c.strength ?? 0), 0) / capabilitiesCount)}%`
    : '—';

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  }, []);

  const displayName = useMemo(() => {
    const raw = user?.name ?? '';
    const stripped = raw.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim();
    return stripped.split(' ')[0] || 'there';
  }, [user]);

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 pb-12 space-y-8">

            {/* ── Header ── */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow mb-2">Dashboard</p>
                <h1 className="text-2xl font-bold leading-tight" style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.02em', color: 'var(--text)', textTransform: 'uppercase' }}>
                  {greeting}, {displayName}
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                  {activeWorkspace ? activeWorkspace.name : 'Capability Operating System'}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => history.push('/graph')}
                  className="btn-ghost hidden sm:flex"
                >
                  <Icon path={ICONS.graph} className="w-4 h-4" />
                  Graph
                </button>
                <button
                  type="button"
                  onClick={() => history.push('/capture')}
                  className="btn-primary"
                >
                  <Icon path={ICONS.capture} className="w-4 h-4" />
                  Capture
                </button>
              </div>
            </div>

            {/* ── The CapOS Loop ── */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-5">
                <p className="eyebrow mb-0">The CapOS Loop</p>
                <span className="text-[10px] uppercase tracking-widest hidden sm:inline"
                  style={{ color: 'var(--text-faint)', fontFamily: 'var(--font-display)', letterSpacing: '0.18em' }}>
                  Closed feedback
                </span>
              </div>
              <LoopFlow />
              <p className="text-xs mt-6 leading-relaxed" style={{ color: 'var(--text-muted)', maxWidth: '60ch' }}>
                Most tools optimize for capture or output. CapOS connects them — closing the loop between what you learn and what you become capable of.
              </p>
            </div>

            {/* ── Thread CTA ── */}
            {!isLoading && (
              <div
                className="rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5"
                style={{
                  background: 'linear-gradient(135deg, var(--crimson-dim) 0%, var(--bg-surface) 100%)',
                  border: '1px solid var(--crimson-border)',
                }}
              >
                <div className="flex-1 min-w-0">
                  <p className="eyebrow mb-1" style={{ color: 'var(--crimson-light)' }}>
                    {capabilitiesCount === 0 ? 'Get started' : 'New Thread'}
                  </p>
                  <h2 className="text-lg font-bold mb-1.5"
                    style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {capabilitiesCount === 0 ? 'Build your Signature' : 'Start a new Thread'}
                  </h2>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)', maxWidth: '52ch' }}>
                    {capabilitiesCount === 0
                      ? "Tell us what you're working toward and we'll map your capabilities — the skills, experiences, and patterns that define you."
                      : 'Define a new goal or problem. Signature will map the capabilities relevant to it and surface what you already have.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => history.push('/onboard')}
                  className="btn-primary flex-shrink-0 whitespace-nowrap"
                >
                  {capabilitiesCount === 0 ? 'Start mapping' : 'New Thread'}
                  <Icon path={ICONS.arrow} className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* ── Stats (real data; hidden for new users) ── */}
            {showStats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Capabilities', value: capabilitiesCount },
                  { label: 'Collections', value: collectionsCount },
                  { label: 'Growing', value: growingCount },
                  { label: 'Avg Strength', value: avgStrength },
                ].map(({ label, value }) => (
                  <div key={label} className="card p-4">
                    <p className="text-[10px] uppercase tracking-widest mb-1"
                      style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)' }}>
                      {label}
                    </p>
                    {statsLoading ? (
                      <div className="h-7 w-12 rounded animate-pulse" style={{ background: 'var(--bg-high)' }} />
                    ) : (
                      <p className="text-2xl font-bold"
                        style={{ fontFamily: 'var(--font-display)', color: 'var(--text)' }}>
                        {value}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ── Modules grid ── */}
            <div>
              <p className="eyebrow mb-4">Modules</p>

              {/* Knowledge layer */}
              <p className="text-[10px] uppercase tracking-widest mb-2 ml-0.5" style={{ color: 'var(--text-faint)', fontFamily: 'var(--font-display)' }}>
                Knowledge
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <ModuleCard
                  label="Notes"
                  description="Capture, connect, and develop your ideas with a linked editor."
                  icon={<Icon path={ICONS.notes} className="w-[18px] h-[18px]" />}
                  path="/notes"
                />
                <ModuleCard
                  label="Thinking"
                  description="Question assumptions, develop insights, and crystallize what you know."
                  icon={<Icon path={ICONS.thinking} className="w-[18px] h-[18px]" />}
                  soon
                />
                <ModuleCard
                  label="Graph"
                  description="See your knowledge as a living network of connected concepts."
                  icon={<Icon path={ICONS.graph} className="w-[18px] h-[18px]" />}
                  path="/graph"
                  stat={capabilities.length || undefined}
                  statLabel="nodes"
                  accent={capabilities.length > 0}
                />
              </div>

              {/* Decisions layer */}
              <p className="text-[10px] uppercase tracking-widest mb-2 ml-0.5" style={{ color: 'var(--text-faint)', fontFamily: 'var(--font-display)' }}>
                Decisions
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                <ModuleCard
                  label="Decision Journal"
                  description="Record decisions with context, options considered, and expected outcomes. Close the loop when reality arrives."
                  icon={<Icon path={ICONS.decisions} className="w-[18px] h-[18px]" />}
                  soon
                />
                <ModuleCard
                  label="Reviews"
                  description="Daily, weekly, and monthly reviews to reflect and recalibrate. Build the compound habit of self-awareness."
                  icon={<Icon path={ICONS.reviews} className="w-[18px] h-[18px]" />}
                  soon
                />
              </div>

              {/* Relationships + Projects layer */}
              <p className="text-[10px] uppercase tracking-widest mb-2 ml-0.5" style={{ color: 'var(--text-faint)', fontFamily: 'var(--font-display)' }}>
                People &amp; Projects
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <ModuleCard
                  label="Relationships"
                  description="Track people, organizations, and meetings. Know your network."
                  icon={<Icon path={ICONS.people} className="w-[18px] h-[18px]" />}
                  soon
                />
                <ModuleCard
                  label="Projects"
                  description="Goals, initiatives, and execution. From intention to outcome."
                  icon={<Icon path={ICONS.projects} className="w-[18px] h-[18px]" />}
                  soon
                />
                <ModuleCard
                  label="Capabilities"
                  description="Your compound capability layer — the skills, knowledge, and leverage you've built."
                  icon={<Icon path={ICONS.capabilities} className="w-[18px] h-[18px]" />}
                  path="/capabilities"
                  stat={capabilities.length > 0 ? capabilities.length : undefined}
                  statLabel={capabilities.length > 0 ? 'total' : undefined}
                  accent={capabilities.length > 0}
                />
              </div>
            </div>

            {/* ── Quick actions ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => history.push('/graph')}
                className="card p-4 flex items-center gap-4 text-left group hover:border-crimson-500 transition-colors"
                style={{ borderColor: 'var(--border-mid)' }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--crimson-dim)', border: '1px solid var(--crimson-border)' }}>
                  <Icon path={ICONS.graph} className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Open Graph
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                    See your capability network
                  </p>
                </div>
                <Icon path={ICONS.arrow} className="w-4 h-4 flex-shrink-0 opacity-40 group-hover:opacity-100 transition-opacity" />
              </button>

              <button
                type="button"
                onClick={() => history.push('/capabilities')}
                className="card p-4 flex items-center gap-4 text-left group"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--bg-high)', border: '1px solid var(--border)' }}>
                  <Icon path={ICONS.capabilities} className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Capabilities
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                    {capabilities.length > 0 ? `${capabilities.length} in your graph` : 'Start building your graph'}
                  </p>
                </div>
                <Icon path={ICONS.arrow} className="w-4 h-4 flex-shrink-0 opacity-40 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>

          </div>
    </AppShell>
  );
};

export default DashboardPage;
